import { useCallback, useState } from 'react';

import log from 'loglevel';

import { useActiveInboxEntriesSlice } from './useActiveInboxEntriesSlice';
import { useMoveInboxEntryToTaskSubgroupService } from '../../services/inboxEntries.service';
import { useInboxEntriesStore } from '../../store';
import {
  clearOptimisticMove,
  recordOptimisticMove,
} from '../../utils/optimisticMoveGuard';

const latestMoveTokens = new Map<string, number>();

export const useMoveInboxEntryToTaskSubgroup = () => {
  const { moveInboxEntryToTaskSubgroup: moveService } =
    useMoveInboxEntryToTaskSubgroupService();
  const { updateInboxEntry, assignInboxEntryToSubgroup } =
    useActiveInboxEntriesSlice();
  const [isMovingToTaskSubgroup, setIsMovingToTaskSubgroup] = useState(false);

  const moveToTaskSubgroup = useCallback(
    async (
      inboxEntryId: string,
      taskSubgroupId: string | null,
    ): Promise<boolean> => {
      const activeTaskGroup =
        useInboxEntriesStore.getState().taskManager.activeTaskGroup;
      const capturedTaskGroupId = activeTaskGroup.taskGroupId;
      const previousSubgroupId =
        activeTaskGroup.inboxEntries.find((e) => e.id === inboxEntryId)
          ?.taskSubgroupId ?? null;

      if (previousSubgroupId === taskSubgroupId) {
        return true;
      }

      // A newer move for the same entry must win: an older in-flight move's
      // response would otherwise apply after and flash the card back.
      const moveToken = (latestMoveTokens.get(inboxEntryId) ?? 0) + 1;
      latestMoveTokens.set(inboxEntryId, moveToken);
      const isLatestMove = () =>
        latestMoveTokens.get(inboxEntryId) === moveToken;

      setIsMovingToTaskSubgroup(true);
      assignInboxEntryToSubgroup(inboxEntryId, taskSubgroupId);
      const movedEntry = useInboxEntriesStore
        .getState()
        .taskManager.activeTaskGroup.inboxEntries.find(
          (entry) => entry.id === inboxEntryId,
        );
      recordOptimisticMove(inboxEntryId, {
        taskSubgroupId,
        orderKey: movedEntry?.orderKey,
      });

      const revertOptimisticMove = () => {
        if (!isLatestMove()) {
          return;
        }
        clearOptimisticMove(inboxEntryId);
        const currentTaskGroupId =
          useInboxEntriesStore.getState().taskManager.activeTaskGroup
            .taskGroupId;
        if (currentTaskGroupId === capturedTaskGroupId) {
          assignInboxEntryToSubgroup(inboxEntryId, previousSubgroupId);
        }
      };

      try {
        const updatedEntry = await moveService(inboxEntryId, taskSubgroupId);

        if (!updatedEntry) {
          revertOptimisticMove();
          return false;
        }

        const currentTaskGroupId =
          useInboxEntriesStore.getState().taskManager.activeTaskGroup
            .taskGroupId;
        if (isLatestMove() && currentTaskGroupId === capturedTaskGroupId) {
          updateInboxEntry(updatedEntry);
        }

        return true;
      } catch (error) {
        log.error('Error moving inbox entry to task subgroup:', error);
        revertOptimisticMove();

        return false;
      } finally {
        setIsMovingToTaskSubgroup(false);
      }
    },
    [moveService, updateInboxEntry, assignInboxEntryToSubgroup],
  );

  return { moveToTaskSubgroup, isMovingToTaskSubgroup };
};
